import { defineStore } from 'pinia';
import { AxiosError } from 'axios';
import http from '@/services/http';
import { clearTokens } from '@/services/tokenStorage';
import { claimPendingSavedViews } from '@/utils/savedViewMigration';
import type { ModelPermissions, UserResponse } from '@/types/types';
import { appQueryClient } from '@/services/appQueryClient';
import { resetZoneSummaries } from '@/composables/api/useZoneSummaries';
import { resetPlayerSummaries } from '@/composables/api/usePlayerSummaries';
import { resetPendingScheduleWrites } from '@/composables/api/scheduleItems';
import { resetTimeZones } from '@/composables/api/useTimeZones';
import { disconnectEcho } from '@/services/echo';
import { resetSearchDocumentAnnouncements } from '@/services/searchDocumentAnnouncements';
import { useBulkActionRunStore } from '@/stores/bulkActionRunStore';
// recentItemsStore imports this store back. Both use the other only inside functions, so the cycle is safe.
import { useRecentItemsStore } from '@/stores/recentItemsStore';

/**
 * The shape of `auth.userData` once `loadUser` succeeds. Exported so
 * tests can construct fixtures with the right type instead of casting
 * through `as never`, and so consumer composables that need to extend
 * the shape (e.g. usePermissions) have one named anchor to import.
 */
export type AuthUser = {
  id: number;
  email: string;
  first_name: string;
  name: string;
  is_developer: boolean;
  clients_only: boolean;
  permissions: Record<string, ModelPermissions>;
  granted_page_names: string[];
  can_issue_raw_command: boolean;
};

interface State {
  userData?: AuthUser;
  verified: boolean;
}

export const useAuthStore = defineStore('authStore', {
  state: (): State => ({
    userData: undefined,
    verified: true
  }),
  getters: {
    isAuthenticated: (state) => {
      return Boolean(state.userData?.email);
    },
    isVerified: (state) => state.verified
  },
  actions: {
    async loadUser() {
      try {
        const response = await http.get('/api/user').then((response) => response.data as UserResponse);
        this.verified = true;
        // Stamp any not-yet-migrated local saved views with this user NOW, on
        // every authenticated load — not only when a table page's saved-views
        // query later succeeds. That gate left a whole class of sessions
        // unstamped (the frontend-before-backend window, a failed list request,
        // a user who never opened a table page), and the next account on the
        // machine then claimed and uploaded their views. `savedViewMigration`
        // is a leaf module: no store import, no cycle.
        claimPendingSavedViews(response.id);
        this.userData = {
          id: response.id,
          email: response.email,
          first_name: response.first_name,
          name: response.name,
          is_developer: response.is_developer,
          clients_only: response.clients_only,
          permissions: response.permissions ?? {},
          granted_page_names: response.granted_page_names ?? [],
          can_issue_raw_command: response.can_issue_raw_command ?? false,
        };
      } catch (error: unknown) {
        if (
          error instanceof AxiosError &&
          error.response?.status === 403 &&
          error.response?.data.message === 'Your email address is not verified.'
        ) {
          this.verified = false;
        }
        this.userData = undefined;
      }
    },

    async logout() {
      try {
        // Revoke tokens on the server
        await http.delete('/api/token');
      } catch (error) {
        console.error('Error during logout: ', error);
        // Continue with local cleanup even if API call fails
      } finally {
        // Clear tokens from localStorage — which also clears the persisted play
        // queue, since it is session data (see tokenStorage.clearTokens).
        clearTokens();
        // Recently opened records are per user; the next person must not see them.
        // The store reads the signed-in user, so clear before userData goes.
        // A static import, not `await import()`: loading the module on the first
        // logout took seconds under load and timed logout tests out.
        try {
          useRecentItemsStore().clearForCurrentUser();
        } catch (error) {
          console.error('Error clearing recent items during logout: ', error);
        }
        // Clear user data
        this.userData = undefined;

        // And empty the TanStack cache, for the same reason the player is torn
        // down below: logging out does not reload the SPA, so anything held in
        // memory outlives the session that fetched it.
        //
        // Saved views made that concrete. Their queries are `staleTime: Infinity`
        // and their keys carry the entity but not the user, so after A signed out
        // and B signed in on the same browser, B's menu filled with A's private
        // view names and definitions — served from memory, with no request to the
        // owner-only endpoint that would have refused them.
        //
        // Cleared wholesale rather than key by key: every private query has this
        // problem, and a list of keys to forget is a list somebody has to
        // remember to add to. The next user refetches what they are allowed to see.
        appQueryClient.clear();
        // The schedule lookups keep zone and player names, and the schedule
        // writes not yet reindexed, in module memory outside TanStack, so
        // clear() above does not reach them.
        resetZoneSummaries();
        resetPlayerSummaries();
        resetPendingScheduleWrites();
        resetTimeZones();
        // The live-update channels were joined with this user's token and permissions. Drop
        // the socket and forget them, so the next user joins with their own.
        disconnectEcho();
        resetSearchDocumentAnnouncements();
        // The bulk-action run is still in memory after clearTokens removed it from storage.
        // The next user's bulk bar would otherwise follow it, and write it back.
        useBulkActionRunStore().forget();

        // Tear the player down as well. Logging out does NOT reload the SPA, so
        // Pinia outlives the route change: `attachAudio(null)` on unmount calls
        // `suspend()`, which deliberately KEEPS the queue, and the next user's
        // `restoreFromPersistence()` early-returns on a non-empty queue. So
        // without this the next person sees the previous user's queue out of
        // memory — and their audio simply keeps playing, on the login screen.
        //
        // Dynamically imported for the same reason the router below is: a static
        // import drags the player store into every module that touches the auth
        // store, which breaks the partially-mocked test files (SAU-287). Guarded
        // because a failure here must not strand the user on a logged-out app.
        try {
            const { useAudioPlaybackStore } = await import('@/stores/playerStore');
            useAudioPlaybackStore().close();
        } catch (error) {
            console.error('Error tearing down the player during logout: ', error);
        }
        // Redirect to login. Imported lazily: a static `import router` here
        // completes an import cycle (router guards import this store), and it
        // drags createRouter into every module that touches the auth store —
        // which breaks test files that partially mock vue-router (SAU-287).
        const { default: router } = await import('@/router');
        router.push({name: 'login'});
      }
    }
  }
});
